import { IExportBase } from "@/api";
import { AttachmentType } from "@/api/attachments";
import {
    exportCompareInChart,
    ICompareInChart,
    updateCompareInChart,
} from "@/api/compare-in-chart";
import { ChatSheet } from "@/components/assistant-ui/chat-sheet";
import { ExportChartModal } from "@/components/frontend/ExportChartModal";
import { ActionButton } from "@/components/shared/buttons/ActionButton";
import { Modal } from "@/components/shared/Modal";
import { TextField } from "@/components/shared/TextField";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { comparisonHasFields } from "@/utils/comparison";
import EditIcon from "@mui/icons-material/Edit";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { Box, Menu, MenuItem, useTheme } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { MessageSquare } from "lucide-react";
import React from "react";
import { trackComparisonExported } from "@/lib/analytics";

interface IProps {
    comparison: ICompareInChart;
}

export const PageHeaderActions = ({ comparison }: IProps) => {
    const [chatDrawerOpen, setChatDrawerOpen] = React.useState(false);
    const [exportModalOpen, setExportModalOpen] = React.useState(false);
    const [moreMenuAnchor, setMoreMenuAnchor] =
        React.useState<null | HTMLElement>(null);
    const [renameModalOpen, setRenameModalOpen] = React.useState(false);
    const [newName, setNewName] = React.useState("");
    const { settings: organizationSettings } = useOrganization();
    const theme = useTheme();
    const queryClient = useQueryClient();

    const handleChatOpen = () => {
        setChatDrawerOpen(true);
    };

    const handleExport = () => {
        setExportModalOpen(true);
    };

    const handleExportSubmit = async (exportData: IExportBase) => {
        try {
            const blob = await exportCompareInChart(comparison.id, exportData);

            // Download the file
            const extension = "xlsx";
            const mimeType =
                "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

            const title = `comparison_${comparison.id}`;

            const url = window.URL.createObjectURL(
                new Blob([blob], { type: mimeType })
            );
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${title}.${extension}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            trackComparisonExported({
                comparison_id: comparison.id,
                export_type: exportData.export_type,
                scope_count: comparison.scopes.length,
                clause_count: exportData.clauses.length,
                include_guidance: exportData.include_guidance,
                include_comments: exportData.include_comments,
                include_metadata: exportData.include_metadata,
                include_fields: exportData.include_fields,
            });

            setExportModalOpen(false);
        } catch (error) {
            console.error("Export failed:", error);
        }
    };

    const handleMoreMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setMoreMenuAnchor(event.currentTarget);
    };

    const handleMoreMenuClose = () => {
        setMoreMenuAnchor(null);
    };

    const handleRenameClick = () => {
        setNewName(comparison.name);
        setRenameModalOpen(true);
        handleMoreMenuClose();
    };

    const handleRename = () => {
        if (newName.trim() && newName !== comparison.name) {
            renameMutation.mutate(newName.trim());
        }
    };

    // Rename mutation - must be before any conditional returns
    const renameMutation = useMutation({
        mutationFn: (newName: string) =>
            updateCompareInChart(comparison.id, { name: newName }),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ["frontend-chart", comparison.id.toString()],
            });
            setRenameModalOpen(false);
            setNewName("");
        },
    });

    return (
        <>
            {organizationSettings.ai_chat_enabled && (
                <ActionButton
                    icon={<MessageSquare />}
                    onClick={handleChatOpen}
                    sx={{
                        backgroundColor: theme.palette.secondary.main,
                        "&:hover": {
                            backgroundColor: theme.palette.secondary.dark,
                        },
                    }}
                >
                    Ask Polly
                </ActionButton>
            )}
            <ActionButton
                icon={<FileDownloadIcon />}
                onClick={handleExport}
                disabled={
                    !comparison.scopes.length ||
                    !comparison.scopes.every(
                        (scope) =>
                            scope.clause_checklist_id ===
                            comparison.scopes[0].clause_checklist_id
                    )
                }
                sx={{
                    backgroundColor: theme.palette.primary.main,
                    "&:hover": {
                        backgroundColor: theme.palette.primary.dark,
                    },
                }}
            >
                Export
            </ActionButton>
            <Box sx={{ position: "relative" }}>
                <ActionButton
                    icon={<MoreVertIcon />}
                    onClick={handleMoreMenuOpen}
                    sx={{
                        backgroundColor: theme.palette.grey[600],
                        "&:hover": {
                            backgroundColor: theme.palette.grey[700],
                        },
                    }}
                >
                    More
                </ActionButton>
                <Menu
                    anchorEl={moreMenuAnchor}
                    open={Boolean(moreMenuAnchor)}
                    onClose={handleMoreMenuClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    sx={{
                        mt: 0.5,
                        "& .MuiPaper-root": {
                            borderRadius: "8px",
                            boxShadow:
                                "0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)",
                            minWidth: 220,
                        },
                    }}
                >
                    <MenuItem
                        onClick={handleRenameClick}
                        sx={{
                            fontSize: "0.75rem",
                            py: 1,
                            px: 1.5,
                            gap: 1,
                            "&:hover": {
                                backgroundColor: theme.palette.grey[50],
                            },
                        }}
                    >
                        <EditIcon
                            sx={{
                                fontSize: 14,
                                color: theme.palette.text.secondary,
                            }}
                        />
                        Rename This Comparison
                    </MenuItem>
                </Menu>
            </Box>

            {/* Export Chart Modal */}
            <ExportChartModal
                open={exportModalOpen}
                onClose={() => setExportModalOpen(false)}
                scopes={comparison.scopes}
                hasFields={comparisonHasFields(comparison)}
                onExport={handleExportSubmit}
            />

            <ChatSheet
                attachment={{
                    id: comparison.id,
                    name: comparison.name,
                    type: AttachmentType.Comparison,
                }}
                open={chatDrawerOpen}
                onClose={() => setChatDrawerOpen(false)}
            />

            {/* Rename Modal */}
            <Modal
                open={renameModalOpen}
                onClose={() => setRenameModalOpen(false)}
                title="Rename This Comparison"
                actions={[
                    {
                        label: "Rename",
                        onClick: handleRename,
                        variant: "contained",
                        color: "primary",
                        disabled:
                            !newName.trim() ||
                            newName === comparison.name ||
                            renameMutation.isPending,
                    },
                ]}
            >
                <TextField
                    label="Comparison Name"
                    value={newName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setNewName(e.target.value)
                    }
                    onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter") {
                            handleRename();
                        }
                    }}
                    autoFocus
                    sx={{ width: "100%" }}
                />
            </Modal>
        </>
    );
};
